/**
 * PATH: apps/quote/src/config/tabs.ts
 * PURPOSE: Quote Hub tab configuration for the shared tab system.
 */

import { createTabStore, type TabNavigationConfig } from '@quotech/ui'
import {
  LayoutDashboard,
  FileText,
  Hammer,
  DollarSign,
  Building2,
  FileStack,
  Receipt,
} from 'lucide-react'

// ─── Tab Store ──────────────────────────────────────────────
export const useQuoteTabStore = createTabStore({
  hubId: 'quote',
  defaultTab: { path: '/quotes', title: 'Quotes', iconKey: 'FileText' },
})

// ─── Icon Map ───────────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  FileText,
  Hammer,
  DollarSign,
  Building2,
  FileStack,
  Receipt,
}

// ─── Route Map ──────────────────────────────────────────────
const ROUTE_MAP: Record<string, { title: string; iconKey: string }> = {
  '/dashboard': { title: 'Dashboard', iconKey: 'LayoutDashboard' },
  '/quotes': { title: 'Quotes', iconKey: 'FileText' },
  '/builder': { title: 'Builder', iconKey: 'Hammer' },
  '/pricelist': { title: 'Pricelist', iconKey: 'DollarSign' },
  '/suppliers': { title: 'Suppliers', iconKey: 'Building2' },
  '/templates': { title: 'Templates', iconKey: 'FileStack' },
  '/claims': { title: 'Claims', iconKey: 'Receipt' },
}

// ─── Config ─────────────────────────────────────────────────
export const QUOTE_TAB_CONFIG: TabNavigationConfig = {
  useStore: useQuoteTabStore,
  routeMap: ROUTE_MAP,
  dynamicRoutes: [
    {
      pattern: /^\/quotes\/(.+)/,
      getMetadata: (path) => ({
        title: `Quote #${path.split('/')[2]}`,
        iconKey: 'FileText',
      }),
    },
    {
      pattern: /^\/builder\/(.+)/,
      getMetadata: (path) => ({
        title: `Builder #${path.split('/')[2]}`,
        iconKey: 'Hammer',
      }),
    },
    {
      pattern: /^\/templates\/(.+)/,
      getMetadata: (path) => ({
        title: `Template #${path.split('/')[2]}`,
        iconKey: 'FileStack',
      }),
    },
    {
      pattern: /^\/claims\/(.+)/,
      getMetadata: (path) => ({
        title: `Claim #${path.split('/')[2]}`,
        iconKey: 'Receipt',
      }),
    },
  ],
  excludedPaths: ['/sso', '/login', '/settings'],
  defaultNewTabPath: '/quotes',
  iconMap: ICON_MAP,
}
